<template>
    <div
        ref="emailInput"
        class="email-input"
        :class="{ 'is-active': isFilled || (isFilled && !$parent.allInputsBlurred) }"
    >
        <label :for="inputId" class="typographies-paragraph-medium-regular" :class="labelClass">
            {{ label }}
        </label>
        <div ref="inputContainer" class="email-input__input" @click="onFocus" @keydown.esc="onBlur">
            <slot name="tags" />
            <input
                v-if="!shouldHideInput"
                :id="inputId"
                :ref="inputId"
                v-model="input"
                :placeholder="
                    isFocused && !isFilled
                        ? $t.__('email.emailPanels.EmailInput.enter_recipient')
                        : ''
                "
                class="typographies-paragraph-medium-regular"
                @input="onInput"
                @focus="onFocus"
                @keydown.,.prevent="onValidateEmail"
                @keydown.tab.prevent="onValidateEmail"
                @keydown.enter.prevent="onValidateEmail"
                @keydown.down="onNavigation('down')"
                @keydown.up="onNavigation('up')"
            />
        </div>
        <div class="actions" :class="{ 'all-line': emailAddresses.length > 0 && isFocused }">
            <slot name="actions"></slot>
        </div>
        <EmailInputDropdown
            v-show="hasDropdownOptions && isFocused"
            :id="`inputDropdown${hashPanel}`"
            :ref="`inputDropdown${hashPanel}`"
            :modelValue="emailAddresses.flatMap((item) => item.id)"
            dropdownClass="email-input__dropdown"
            :parentRef="inputId"
            :minWidth="0"
            :isVisible="hasDropdownOptions && isFocused"
            :loading="isLoading"
        >
            <div>
                <CompassOptionGroup
                    v-if="recommendedEmails.length"
                    :label="$t.__('email.emailPanels.EmailInput.suggested')"
                >
                    <CompassDropdownOption
                        v-for="(item, index) in recommendedEmails"
                        :key="`rec-${item.type}-${item.id || index}`"
                        :label="`${item.name} (${item.email})`"
                        :value="item.uuid"
                        class="dropdown-email__option"
                        @item-selected="onItemSelected(item)"
                    >
                        <CompassAvatar
                            :initials="`${item.firstName.charAt(0)}${item.lastName.charAt(0)}`"
                            :photo="getPhotoAvatar(item.avatar)"
                            :colorType="getAvatarRandomColor(item)"
                            size="extra-small"
                        />
                        <div class="dropdown-email__avatar-text">
                            <span
                                class="avatar-name"
                                :class="{
                                    'email-already-added': checkIfEmailAlreadyAdded(item.email),
                                }"
                            >
                                {{ item.firstName }} {{ item.lastName }}
                                <span v-if="item.position">({{ item.position }})</span>
                            </span>
                            <span class="avatar-email typographies-paragraph-small-regular">
                                {{ item.email }}
                            </span>
                        </div>
                        <CompassIcon
                            v-if="checkIfEmailAlreadyAdded(item.email)"
                            icon="check"
                            type="link"
                            class="dropdown-email__check-icon"
                        />
                    </CompassDropdownOption>
                </CompassOptionGroup>
                <CompassOptionGroup
                    v-if="dataFromAutocomplete.length"
                    :label="$t.__('email.emailPanels.EmailInput.all_results')"
                >
                    <CompassDropdownOption
                        v-for="(item, index) in dataFromAutocomplete"
                        :key="`res-${item.type}-${item.id || index}`"
                        :label="`${item.name} (${item.email})`"
                        :value="item.uuid"
                        :hovered="itemHovered === recommendedEmails.length + index"
                        class="dropdown-email__option"
                        @item-selected="onItemSelected(item)"
                    >
                        <CompassAvatar
                            :initials="`${item.firstName.charAt(0)}${item.lastName.charAt(0)}`"
                            :photo="getPhotoAvatar(item.avatar)"
                            :colorType="getAvatarRandomColor(item)"
                            size="extra-small"
                        />
                        <div class="dropdown-email__avatar-text">
                            <span
                                class="avatar-name"
                                :class="{
                                    'email-already-added': checkIfEmailAlreadyAdded(item.email),
                                }"
                            >
                                {{ item.firstName }} {{ item.lastName }}
                                <span v-if="item.position">({{ item.position }})</span>
                            </span>
                            <span class="avatar-email typographies-paragraph-small-regular">
                                {{ item.email }}
                            </span>
                        </div>
                    </CompassDropdownOption>
                </CompassOptionGroup>
            </div>
        </EmailInputDropdown>
    </div>
</template>
<script>
import generateHash from 'Helpers/hashGenerator.js';
import EmailInputDropdown from './EmailInputDropdown.vue';
import { randAvatarColorFullname } from 'utils/avatar';
import { createNamespacedHelpers } from 'vuex';
const { mapState: mapStateUser } = createNamespacedHelpers('user');
import { validateEmail } from 'Helpers/validator/EmailValidator';
import { searchRecipients } from 'api/emailEngine/emailEngineService';
import { debounce } from 'lodash';
import { onClickOutside } from '@vueuse/core';

export default {
    components: { EmailInputDropdown },
    props: {
        label: {
            type: String,
            required: true,
        },
        type: {
            type: String,
            required: true,
        },
        emailAddresses: {
            type: Array,
            required: true,
        },
        isFocused: {
            type: Boolean,
            required: false,
            default: false,
        },
        shouldHideInput: {
            type: Boolean,
            required: false,
            default: false,
        },
        hashPanel: {
            type: String,
            required: false,
            default: '',
        },
    },
    emits: ['onAddTag', 'focus', 'blur'],
    data() {
        return {
            input: '',
            inputId: '',
            labelClass: [],
            dataFromAutocomplete: [],
            recommendedEmails: [],
            hash: '',
            dropdownWidth: 0,
            itemHovered: -1,
            needToUpdateAndOpenDropdown: false,
            separatorRegex: /[,;]/,
            isLoading: false,
        };
    },
    computed: {
        ...mapStateUser(['recommendedUsers']),
        isFilled() {
            return !!(this.emailAddresses.length || this.input);
        },
        hasDropdownOptions() {
            return this.recommendedEmails.length + this.dataFromAutocomplete.length > 0;
        },
        totalOptionsCount() {
            return this.recommendedEmails.length + this.dataFromAutocomplete.length;
        },
    },
    watch: {
        isFocused: {
            handler(newFocus) {
                if (newFocus) {
                    this.focusInput();
                    this.checkHeightInput();
                    this.recommendedEmails = this.filterRecommendedUsers();
                } else {
                    this.onValidateEmail();

                    if (this.needToUpdateAndOpenDropdown) {
                        this.needToUpdateAndOpenDropdown = false;
                        this.onFocus();
                        this.checkHeightInput();
                        this.recommendedEmails = this.filterRecommendedUsers();
                    }
                }
            },
            immediate: true,
        },
        'emailAddresses.length': function (newLength) {
            this.$nextTick(() => {
                if (newLength > 0) {
                    this.checkHeightInput();
                }
            });
            this.recommendedEmails = this.filterRecommendedUsers();
        },
        recommendedUsers: {
            immediate: true,
            handler() {
                if (this.isFocused) {
                    this.focusInput();
                    this.checkHeightInput();
                    this.recommendedEmails = this.filterRecommendedUsers();
                }
            },
        },
    },
    mounted() {
        if (this.isFocused) {
            this.focusInput();
        }
        onClickOutside(
            this.$refs.inputContainer,
            (event) => {
                if (this.isFocused) {
                    this.onBlur(event);
                }
            },
            { ignore: [this.$refs[`inputDropdown${this.hashPanel}`]] },
        );
    },
    created() {
        this.hash = generateHash();
        this.inputId = `email-input-${this.type}-${this.hash}`;
    },
    methods: {
        checkIfEmailAlreadyAdded(email) {
            return this.emailAddresses.some((item) => item.email === email);
        },
        filterRecommendedUsers(query = '') {
            if (!this.emailAddresses || !this.recommendedUsers) {
                return [];
            }

            const q = query.trim().toLowerCase();
            return this.recommendedUsers
                .filter((user) => {
                    return !this.emailAddresses.some(
                        (emailAddress) => emailAddress.email === user.email,
                    );
                })
                .filter((user) => {
                    if (!q) return true;
                    const fullName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();
                    return (
                        user.email?.toLowerCase().includes(q) || fullName.toLowerCase().includes(q)
                    );
                });
        },
        onNavigation(direction) {
            if (!this.totalOptionsCount) return;
            if (direction === 'down') {
                this.itemHovered =
                    (this.itemHovered + 1 + this.totalOptionsCount) % this.totalOptionsCount;
            } else {
                this.itemHovered =
                    (this.itemHovered - 1 + this.totalOptionsCount) % this.totalOptionsCount;
            }
        },
        focusInput() {
            this.$nextTick(() => {
                document.getElementById(this.inputId)?.focus();
            });
        },
        onItemSelected(contact) {
            this.addEmailAddress(contact);
        },
        onInput: debounce(function () {
            this.recommendedEmails = this.filterRecommendedUsers(this.input);
            if (this.input === '') {
                this.dataFromAutocomplete = [];
                this.isLoading = false;
                this.itemHovered = this.recommendedEmails.length ? 0 : -1;
                return;
            }

            this.isLoading = true;
            searchRecipients(this.input)
                .then((response) => {
                    this.dataFromAutocomplete = response.data.map((item) => ({
                        email: item.email,
                        firstName: item.first_name,
                        lastName: item.last_name,
                        position: item.object.type === 'staff' ? null : item.position,
                        id: item.object.id,
                        type: item.object.type,
                        avatar: item.avatar ?? null,
                        uuid: generateHash(),
                    }));

                    // Exclude already-added emails
                    this.dataFromAutocomplete = this.dataFromAutocomplete.filter((item) =>
                        this.emailAddresses.every((email) => email.email !== item.email),
                    );
                    this.dataFromAutocomplete = this.dataFromAutocomplete.filter(
                        (item) => !this.recommendedEmails.some((u) => u.email === item.email),
                    );
                })
                .catch(() => {
                    this.$pushToast({
                        type: 'error',
                        close: true,
                        text: this.$t.__('email.emailPanels.EmailInput.search_recipients_error'),
                    });
                })
                .finally(() => {
                    this.isLoading = false;
                    this.itemHovered = this.totalOptionsCount ? 0 : -1;
                    if (this.totalOptionsCount > 0) {
                        requestAnimationFrame(() => {
                            const dropdownElement = this.$refs[`inputDropdown${this.hashPanel}`];
                            const widthSellsyApp =
                                document.getElementById('sellsy_app').offsetWidth;
                            const dropdownLeftPosition =
                                dropdownElement.$el.getBoundingClientRect().left;
                            this.dropdownWidth = widthSellsyApp - (dropdownLeftPosition + 48);
                        });
                    }
                });
        }, 400),
        onFocus() {
            this.focusInput();
            this.$emit('focus');
        },
        hasWhiteSpace(s) {
            return /\s/g.test(s);
        },
        onBlur() {
            this.dataFromAutocomplete = [];
            this.itemHovered = -1;
            this.onValidateEmail();
            const isFilled = !!this.emailAddresses.length || !!this.input;
            this.$emit('blur', isFilled);
        },
        isValidEmail(email) {
            return validateEmail(email) && !this.checkIfEmailAlreadyAdded(email);
        },
        onValidateEmail() {
            const emailInput = this.input.trim();
            if (this.totalOptionsCount > 0 && this.itemHovered > -1) {
                const idx = this.itemHovered;
                const isInRecommended = idx < this.recommendedEmails.length;
                const selectedItem = isInRecommended
                    ? this.recommendedEmails[idx]
                    : this.dataFromAutocomplete[idx - this.recommendedEmails.length];
                if (selectedItem) {
                    this.onItemSelected(selectedItem);
                    return;
                }
            } else if (this.hasEmailSeparator(emailInput)) {
                for (const email of this.getEmailsArray(emailInput)) {
                    this.addNotRegisteredEmail(email);
                }
            } else if (emailInput.length && !this.hasWhiteSpace(emailInput)) {
                this.addNotRegisteredEmail(emailInput);
            }
        },
        hasEmailSeparator(emailInput) {
            return this.separatorRegex.test(emailInput);
        },
        getEmailsArray(emailInput) {
            return new Set(
                emailInput
                    .split(this.separatorRegex)
                    .map((email) => email.trim())
                    .filter((email) => email.length),
            );
        },
        addNotRegisteredEmail(email) {
            const recipient = {
                email: email,
                isValid: this.isValidEmail(email),
                type: this.type,
                id: generateHash(),
            };

            this.$emit('onAddTag', recipient);
            this.input = '';
        },
        addEmailAddress(contact = null) {
            if (
                this.emailAddresses.some((email) => email.address === contact?.email ?? this.input)
            ) {
                return;
            }
            const recipient = {
                email: contact?.email ?? this.input,
                isValid: validateEmail(contact?.email ?? this.input),
                type: this.type,
                id: contact.uuid,
            };
            if (contact) {
                recipient.name = `${contact?.firstName} ${contact?.lastName}`;
                recipient.related = [
                    {
                        id: contact?.id,
                        type: contact?.type,
                    },
                ];
            }
            this.dataFromAutocomplete = [];
            this.$emit('onAddTag', recipient);
            window.EventBus.emit(`richText-${this.hashPanel}:onAddTag`, recipient);
            this.input = '';
        },
        checkHeightInput() {
            this.$nextTick(() => {
                const emailInput = this.$refs.emailInput;
                this.labelClass = emailInput?.clientHeight > 32 ? ['alignStart'] : [''];
            });
        },
        getFullName(item) {
            return `${item.firstName} ${item.lastName}`;
        },
        getAvatarRandomColor(item) {
            return randAvatarColorFullname(this.getFullName(item));
        },
        getPhotoAvatar(avatar) {
            return avatar?.type === 'picture' ? avatar.value : '';
        },
    },
};
</script>

<style lang="scss" scoped>
.email-input {
    align-items: center;
    border-bottom: 1px solid var(--colors-common-border-lighter);
    display: flex;
    gap: var(--spacing-1);
    padding: var(--spacing-0) 0;
    line-height: var(--line-heights-24);

    &:has(input:focus) {
        border-color: var(--colors-common-border-focus);
        label {
            color: var(--colors-common-text-standard-default);
            font-weight: var(--font-weights-lato-1);
        }
    }

    &.is-active {
        label {
            display: flex;
            align-items: center;
            height: 24px;
            color: var(--colors-common-text-standard-default);
            font-weight: var(--font-weights-lato-1);
        }
    }

    label {
        display: flex;
        align-items: center;
        height: 24px;
        user-select: none;
        color: var(--colors-common-text-standard-placeholder);
        &.alignStart {
            align-self: start;
        }
    }

    input {
        border: none !important;
        flex-grow: 1;
        margin-left: var(--spacing-1);
        padding: 0 !important;
        text-overflow: ellipsis;
    }

    &__input {
        display: flex;
        flex-direction: row;
        flex-grow: 1;
        flex-wrap: wrap;
        gap: var(--spacing-0);
    }

    .actions {
        display: flex;
        align-items: center;
        flex-shrink: 0;
        align-self: start;
        margin-left: auto;
        height: 24px;
    }

    &__dropdown {
        z-index: 1001;
        max-width: 312px;
        .compass-dropdown-option {
            padding-top: var(--spacing-1);
            padding-bottom: var(--spacing-1);
        }

        .dropdown-section-header {
            color: var(--colors-common-text-standard-caption);
            font-weight: 600;
            font-size: 12px;
            margin: var(--spacing-2) var(--spacing-1) auto var(--spacing-1);
        }

        .dropdown-email__avatar-text {
            display: flex;
            flex-direction: column;
            .avatar-name {
                line-height: 17px;
                &.email-already-added {
                    font-weight: 600;
                }
            }
            .avatar-email {
                color: var(--colors-common-text-standard-caption);
            }
        }

        .dropdown-email__option {
            position: relative;
        }

        .dropdown-email__check-icon {
            position: absolute;
            right: var(--spacing-2);
            top: 50%;
            transform: translateY(-50%);
        }
    }
}
</style>
